import React, { useEffect, useState } from "react";
import theme from "../../../styles/theme";

// Import du composant CoreliaTable qui sera utilisé pour afficher les données dans un tableau
import CoreliaTable from "../Corelia_standard/CoreliaTable"; 

/* ============================================================================ 
   COSTING BOARD — CORELIA 
   ----------------------------------------------------------------------------
   Composant principal de la page de gestion des coûts dans le tableau de bord.
============================================================================ */

// Définition des styles basés sur le thème de l'application
const T = {
  text: theme?.colors?.text?.primary || "#164D63", // Couleur du texte principal
  soft: theme?.colors?.text?.small || "#5C7A89", // Couleur du texte secondaire (petit texte)
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1", // Couleur pour les textes très petits
  border: theme?.colors?.borders?.default || "#d6dee4", // Couleur des bordures
  corelia: theme?.colors?.text?.corelia || "#0e3f56", // Couleur principale de l'application
};

// Définition des styles CSS pour la mise en page
const S = {
  page: {
    width: "100%", // Prend toute la largeur de la page
    height: "100%", // Prend toute la hauteur de la page
    minWidth: 0,
    minHeight: 0,
    display: "grid", // Utilisation de grid pour organiser les éléments
    gridTemplateRows: "auto minmax(0, 348px) minmax(0, 1fr)", // Définition des lignes du grid
    gap: 10, // Espace entre les éléments du grid
    overflow: "hidden", // Cacher tout contenu qui dépasse les limites
    boxSizing: "border-box", // Calcul des tailles incluant les bordures
  },

  topBar: {
    width: "100%", // Prend toute la largeur
    minWidth: 0,
    display: "grid", // Utilisation de grid pour organiser la barre du haut
    gridTemplateColumns: "auto 260px 1fr", // Trois colonnes pour la barre du haut
    alignItems: "start", // Aligner les éléments en haut de la cellule
    gap: 10, // Espace entre les éléments
    padding: "2px 0 0 0", // Espacement interne
    boxSizing: "border-box", // Calcul des tailles incluant les bordures
    position: "relative", // Position relative pour gérer le z-index
    zIndex: 3, // Priorité d'affichage de l'élément
  },

  topLabel: {
    fontFamily: theme?.fonts?.body || "inherit", // Police du texte
    fontSize: "0.72rem", // Taille de la police
    lineHeight: 1.1, // Hauteur de ligne
    color: T.soft, // Couleur du texte
    whiteSpace: "nowrap", // Empêche le texte de passer à la ligne
    paddingTop: 6, // Espacement en haut
  },

  select: {
    width: "100%", // Prend toute la largeur du conteneur
    height: 26, // Hauteur de l'élément
    minWidth: 0,
    border: `1px solid ${T.border}`, // Bordure de l'élément avec la couleur définie dans T
    borderRadius: theme?.borders?.radius?.md || "4px", // Rayon de bordure
    background: theme?.colors?.background?.surface || "#ffffff", // Fond de l'élément
    padding: "0 8px", // Espacement interne
    boxSizing: "border-box", // Calcul des tailles incluant les bordures
    outline: "none", // Retire le contour lors du focus
    color: T.text, // Couleur du texte
    fontFamily: theme?.fonts?.body || "inherit", // Police utilisée
    fontSize: "0.72rem", // Taille de la police
  },
};

// Exemple de données à afficher dans le tableau
const exampleData = [
  {
    id: "r1", // Identifiant unique
    code: "1.1", // Code de l'article
    article: "Supply HDPE Pipe DN400 PN16", // Nom de l'article
    type: "Material", // Type d'élément
    unit: "m", // Unité de mesure
    driver: "Manual", // Mode de saisie
    quantity: "1200", // Quantité
    cost: "5000", // Coût estimé
    total: "6000", // Coût total
    source: "Manual", // Source des données
    factor: "1.00", // Facteur d'ajustement
  },
  {
    id: "r2", // Autre ligne d'exemple
    code: "1.2",
    article: "Installation HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    driver: "From parent",
    quantity: "1200",
    cost: "2000",
    total: "2400",
    source: "Inherited",
    factor: "1.00",
  },
  // D'autres lignes similaires...
];

// Composant pour afficher une ligne dans le tableau
const TableRow = ({ row }) => (
  <tr key={row.id}>
    <td>{row.code}</td>
    <td>{row.article}</td>
    <td>{row.type}</td>
    <td>{row.unit}</td>
    <td>{row.quantity}</td>
    <td>{row.cost}</td>
    <td>{row.total}</td>
    <td>{row.source}</td>
    <td>{row.factor}</td>
  </tr>
);

const CostingBoard = ({ onBannerDataChange }) => {
  const [rows, setRows] = useState(exampleData); // Utilisation du hook useState pour gérer les lignes du tableau


    // ===========================
  //  BANNER
  // ===========================
  // Effet qui met à jour le banner
  useEffect(() => {
    if (typeof onBannerDataChange !== "function") return; // Vérifie que la fonction onBannerDataChange existe

    // Mise à jour des données du banner
    onBannerDataChange({
      metrics: [{ label: "Total Cost", value: "To be calculated", valueColor: T.corelia }],
      showMetrics: true,
      showFilters: true,
    });
  }, [onBannerDataChange]);

  return (
    <div style={S.page}> {/* Conteneur principal de la page */}
      <div style={S.topBar}> {/* Barre du haut */}
        <div style={S.topLabel}>Package</div> {/* Label pour le package */}
        <div>
          {/* Conteneur horizontal avec 2 divs */}
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div style={{ flex: 1, padding: "6px" }}>
              {/* Utilisation du tableau CoreliaTable avec les données exemple */}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Article</th>
                    <th>Type</th>
                    <th>Unit</th>
                    <th>Quantity</th>
                    <th>Cost</th>
                    <th>Total</th>
                    <th>Source</th>
                    <th>Factor</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <TableRow key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ flex: 1, padding: "6px" }}>
              {/* Espace pour d'autres éléments si besoin */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostingBoard;